import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { shareBill, shareQRPayment, isShareSupported, isFileShareSupported } from '../utils/shareUtils';

/**
 * Reusable hook for sharing order bills and QR payments
 * Can be used in OrderManagement, CustomerManagement, or any other component
 */
const useShare = (shopSettings) => {
  const [sharing, setSharing] = useState(false);
  const [shareType, setShareType] = useState(null); // 'bill' or 'qr'
  const [sharingOrderId, setSharingOrderId] = useState(null);

  // Check if share is supported
  const canShare = isShareSupported();
  const canShareFiles = isFileShareSupported();

  // Share bill
  const handleShareBill = useCallback(async (order) => {
    if (!order) return;
    
    setSharing(true);
    setShareType('bill');
    setSharingOrderId(order._id);
    
    try {
      const result = await shareBill(order, shopSettings);
      
      if (result.method === 'native') {
        // Native share was used, no need to show toast
      }
    } catch (error) {
      console.error('Share bill error:', error);
      toast.error(error.message || 'Không thể chia sẻ hóa đơn');
    } finally {
      setSharing(false);
      setShareType(null);
      setSharingOrderId(null);
    }
  }, [shopSettings]);

  // Share QR payment
  const handleShareQR = useCallback(async (order) => {
    if (!order) return;
    
    // Check if bank account is configured
    const defaultBank = shopSettings?.bankAccounts?.find(b => b.isDefault);
    if (!defaultBank) {
      toast.error('Chưa cấu hình tài khoản ngân hàng mặc định');
      return;
    }
    
    setSharing(true);
    setShareType('qr');
    setSharingOrderId(order._id);
    
    try {
      const result = await shareQRPayment(order, shopSettings);
      
      if (result.method === 'native') {
        // Native share was used
      }
    } catch (error) {
      console.error('Share QR error:', error);
      toast.error(error.message || 'Không thể chia sẻ mã QR');
    } finally {
      setSharing(false);
      setShareType(null);
      setSharingOrderId(null);
    }
  }, [shopSettings]);

  // Check if a specific order is being shared
  const isSharing = useCallback((orderId, type = null) => {
    if (!sharing) return false;
    if (orderId && sharingOrderId !== orderId) return false;
    if (type && shareType !== type) return false;
    return true;
  }, [sharing, sharingOrderId, shareType]);

  return {
    sharing,
    shareType,
    sharingOrderId,
    canShare,
    canShareFiles,
    handleShareBill,
    handleShareQR,
    isSharing
  };
};

export default useShare;
